/**
 * Rendering a verb's time cost.
 *
 * **The number itself is never computed here.** Rounds and minutes both come
 * from the server, which reads them out of the structured ruleset; this file
 * only decides what the button says. That split is the point: a cost is a
 * rule, and a client that did the arithmetic would be a second source of truth
 * for it.
 *
 * Showing the cost at all is a usability requirement rather than a nicety —
 * without it players click Look meaning Search, find nothing, and conclude the
 * game is broken.
 */

import type { VerbDescriptor } from '@portal/shared';

/**
 * The short label on the button — `10m`, `free`, or `varies`.
 *
 * `varies` is deliberately not `0` and not blank. The ruleset gives no figure
 * for climbing or resting, and the DM reports an elapsed band for the beat
 * instead; rendering that as free would be inventing a rule, and rendering
 * nothing would read as an oversight.
 */
export function verbCostLabel(verb: VerbDescriptor): string {
  // **A printed cost that happens to be dice is not `varies`.** Disarming a
  // trap costs 1d10 rounds and the book says so, where climbing has no figure
  // at all — collapsing the two would leave the band to answer for something
  // the ruleset already decided.
  if (verb.roundsDice) return `${verb.roundsDice} rd`;
  if (verb.rounds === undefined) return 'varies';
  if (verb.rounds === 0) return 'free';
  if (verb.minutes !== undefined) return `${verb.minutes}m`;
  // Rounds, labelled as rounds. Falling back to `${rounds}m` would print a
  // count of one unit under the name of another the moment the server stops
  // sending the conversion — silently, and only wrong by a factor.
  return `${verb.rounds} rd`;
}

/**
 * The full explanation, for the button's tooltip.
 *
 * A house rule says so in words. The plan's reason is that a house rule
 * indistinguishable from the book is how a table ends up arguing about the
 * book — the search cost, for instance, is printed but covers only a 20-foot
 * wall section, and the figure for forcing a door is not printed at all.
 */
export function verbCostDetail(verb: VerbDescriptor): string {
  const parts: string[] = [];

  if (verb.roundsDice) {
    parts.push(`Costs ${verb.roundsDice} rounds of world time, rolled when it happens.`);
  } else if (verb.rounds === undefined) {
    parts.push('No time cost in the ruleset — the DM reports how long it took.');
  } else if (verb.rounds === 0) {
    parts.push('Free: costs the world clock nothing.');
  } else {
    parts.push(`Costs ${verb.rounds} round${verb.rounds === 1 ? '' : 's'} of world time.`);
  }

  if (verb.houseRule) parts.push('House rule: this figure is not printed in the book.');
  if (verb.note) parts.push(verb.note);

  return parts.join(' ');
}

/** `pick_pocket` → `Pick pocket`. Presentation only; the id is what is sent. */
export function verbLabel(verb: string): string {
  const spaced = verb.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
